import { useEffect, useState } from 'react';
import { api, ApiError, type User } from '../api';
import { useAuth } from '../auth';
import { Alert, Badge, Button, Card, Input, PageHeader, Spinner } from '../components/ui';

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[] | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  async function load(query = '') {
    try {
      const res = await api.adminUsers(query || undefined);
      setUsers(res.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load users');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function update(email: string, patch: { can_publish?: boolean; role?: 'user' | 'admin' }) {
    setBusyEmail(email);
    setError(null);
    try {
      await api.adminUpdateUser(email, patch);
      await load(q);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Update failed');
    } finally {
      setBusyEmail(null);
    }
  }

  return (
    <div>
      <PageHeader title="User Administration" subtitle="Approve publishers and manage roles." />
      {error && (
        <div className="mb-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      <form
        className="mb-4 flex max-w-md gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void load(q);
        }}
      >
        <Input placeholder="Search by email…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button variant="secondary" type="submit">
          Search
        </Button>
      </form>

      {!users ? (
        <Spinner />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Publish</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isSelf = u.email === me?.email;
                const busy = busyEmail === u.email;
                return (
                  <tr key={u.email}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-800">{u.email}</span>
                      {u.github && <span className="ml-1 text-xs text-slate-400">@{u.github}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {u.role === 'admin' ? <Badge color="purple">admin</Badge> : <Badge>user</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      {u.canPublish ? <Badge color="green">yes</Badge> : <Badge color="amber">no</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => update(u.email, { can_publish: !u.canPublish })}
                        >
                          {u.canPublish ? 'Revoke publish' : 'Approve publish'}
                        </Button>
                        {!isSelf &&
                          (u.role === 'admin' ? (
                            <Button variant="ghost" disabled={busy} onClick={() => update(u.email, { role: 'user' })}>
                              Demote
                            </Button>
                          ) : (
                            <Button variant="ghost" disabled={busy} onClick={() => update(u.email, { role: 'admin' })}>
                              Make admin
                            </Button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
